import dayjs, { Dayjs } from 'dayjs';
import { readableColor } from 'polished';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { settings } from '../../../settings';
import {
  BundleJson,
  BundlesPageJson,
  BundleStatus,
  BundleStatusActive,
} from '../../../types/bundleTypes';
import { OrderStatus, ProductType, Region } from '../../../types/orderTypes';
import fetch from '../../../utils/fetch';
import { emptyMap } from '../../../utils/helpers';
import { createGroupColors } from '../../oldPlanner/planner-map/map/PlannerMapMapUtils';
import { getBundleColor } from '../../oldPlanner/utils/bundleDraft';

export type PlannerData = {
  fetchedBundles: BundleJson[];
  fetchedOrders: PlannerOrder[];
  orders: PlannerOrder[][];
  bundles: PlannerBundle[];
  multiMarkers: PlannerOrder[][];
  windowGroups: WindowGroups;
};

export type PlannerState = {
  loading: boolean;
  refetching: boolean;
  error?: string;
  data?: PlannerData;
};

export type WindowGroups = Map<string, string>;

export type PlannerBundle = {
  id: string;
  orders: PlannerOrder[][];
  color: string;
  textColor: string;
  status: BundleStatus;
};

export type PlannerOrder = {
  id: string;
  deliverFrom: string;
  deliverUntil: string;
  pickupName: string;
  senderName: string;
  scannedAtPartner: string;
  window: string;
  originAddress: {
    fullAddressString: string;
    postalCode: string;
    location: {
      latitude: number;
      longitude: number;
    };
  };
  destinationAddress: {
    fullAddressString: string;
    postalCode: string;
    location: {
      latitude: number;
      longitude: number;
    };
  };
  product: ProductType;
  status: OrderStatus;
  bundleId?: string;
  temperatureControlled?: boolean;
  xxl: boolean;
  idCheck?: boolean;
  deliveryId: string;
  timeWasChanged?: boolean;
  scannedAtHub: string;
};

export function createFeatureStructure(
  fetchedOrders: PlannerOrder[],
  fetchedBundles: BundleJson[]
): {
  ordersFeature: PlannerOrder[][];
  bundleFeature: PlannerBundle[];
  multiMarkersOrderFeature: PlannerOrder[][];
} {
  const tempFilteredOrders: Record<string, PlannerOrder[]> = {};
  const tempFilteredOrdersForBundles: Record<
    string,
    Record<string, PlannerOrder[]>
  > = {};
  const tempFilteredOrdersForMultiMarkers: Record<string, PlannerOrder[]> = {};

  fetchedOrders.forEach(order => {
    const addressKey = `${order.destinationAddress.location.latitude}-${order.destinationAddress.location.longitude}`;
    const bundleIdKey = order.bundleId;
    if (bundleIdKey) {
      if (tempFilteredOrdersForBundles[bundleIdKey]) {
        if (tempFilteredOrdersForBundles[bundleIdKey][addressKey]) {
          tempFilteredOrdersForBundles[bundleIdKey][addressKey].push(order);
        } else {
          tempFilteredOrdersForBundles[bundleIdKey] = {
            ...tempFilteredOrdersForBundles[bundleIdKey],
            [addressKey]: [order],
          };
        }
      } else {
        tempFilteredOrdersForBundles[bundleIdKey] = {
          [addressKey]: [order],
        };
      }
    } else {
      if (tempFilteredOrders[addressKey]) {
        tempFilteredOrders[addressKey].push(order);
      } else {
        tempFilteredOrders[addressKey] = [order];
      }
    }

    if (tempFilteredOrdersForMultiMarkers[addressKey]) {
      tempFilteredOrdersForMultiMarkers[addressKey].push(order);
    } else {
      tempFilteredOrdersForMultiMarkers[addressKey] = [order];
    }
  });

  const ordersFeature: PlannerOrder[][] = Object.keys(tempFilteredOrders).map(
    key => tempFilteredOrders[key]
  );

  const multiMarkersOrderFeature: PlannerOrder[][] = Object.keys(
    tempFilteredOrdersForMultiMarkers
  )
    .map(key => tempFilteredOrdersForMultiMarkers[key])
    .filter(marker => marker.length > 1);

  const bundleFeature: PlannerBundle[] = Object.keys(
    tempFilteredOrdersForBundles
  )
    .map((key: string) => {
      const bundleColor = getBundleColor(key);
      const bundleStatus: BundleStatus = fetchedBundles.find(
        bundle => bundle.id === key
      )?.status;

      return bundleStatus
        ? {
            id: key,
            orders: Object.keys(tempFilteredOrdersForBundles[key]).map(
              address => tempFilteredOrdersForBundles[key][address]
            ),
            color: bundleColor,
            textColor: readableColor(bundleColor, '#000000', '#ffffff'),
            status: bundleStatus,
          }
        : undefined;
    })
    .filter(Boolean);

  return {
    ordersFeature,
    bundleFeature,
    multiMarkersOrderFeature,
  };
}

const plannerDataInitialState: PlannerState = {
  loading: true,
  refetching: false,
  data: {
    fetchedOrders: [],
    fetchedBundles: [],
    orders: [],
    bundles: [],
    multiMarkers: [],
    windowGroups: emptyMap,
  },
  error: '',
};

export const updatePlannerOrders = async (
  region: Region,
  date: Dayjs,
  orderIds: string[]
): Promise<{ updatedOrders: PlannerOrder[]; bundles: BundleJson[] }> => {
  const formattedDate = date.format('YYYY-MM-DD');

  const updatedOrdersResponse = await fetch.post({
    url: `${settings.newApiUrl}/order/planner-orders?start=${formattedDate}&end=${formattedDate}&region=${region}`,
    body: {
      orderIds,
    },
  });
  const bundleResponse = await fetch.get({
    url: `${settings.newApiUrl}/bundle?status=${BundleStatusActive.join(
      ','
    )}&region=${region}`,
  });
  if (updatedOrdersResponse.ok && bundleResponse.ok) {
    const updatedOrders = (await updatedOrdersResponse.json()).map(order => ({
      ...order,
      window: `${dayjs(order.deliverFrom).format('HH:mm')} - ${dayjs(
        order.deliverUntil
      ).format('HH:mm')}`,
    })) as PlannerOrder[];
    const bundlePage = (await bundleResponse.json()) as BundlesPageJson;
    const bundles = bundlePage.items;
    return { updatedOrders, bundles };
  }
  return { updatedOrders: [], bundles: [] };
};

export const usePlannerData = (
  region: Region,
  date: Dayjs
): {
  state: PlannerState;
  reFetch: () => Promise<void>;
  setState: Dispatch<SetStateAction<PlannerState>>;
} => {
  const [state, setState] = useState<PlannerState>(plannerDataInitialState);

  const fetchData = useCallback(
    async (refetching = false) => {
      setState(cs => ({
        ...cs,
        loading: true,
        refetching,
      }));

      const formattedDate = date.format('YYYY-MM-DD');

      try {
        const ordersResponse = await fetch.get({
          url: `${settings.newApiUrl}/order/planner-orders?start=${formattedDate}&end=${formattedDate}&region=${region}`,
        });

        const bundleResponse = await fetch.get({
          url: `${settings.newApiUrl}/bundle?status=${BundleStatusActive.join(
            ','
          )}&region=${region}`,
        });

        if (ordersResponse.ok && bundleResponse.ok) {
          const orders = (await ordersResponse.json()).map(order => ({
            ...order,
            window: `${dayjs(order.deliverFrom).format('HH:mm')} - ${dayjs(
              order.deliverUntil
            ).format('HH:mm')}`,
          })) as PlannerOrder[];
          const bundleIdsInOrder = Array.from(
            new Set(
              orders
                .filter(({ bundleId }) => bundleId)
                .map(({ bundleId }) => bundleId)
            )
          );
          const bundlePage = (await bundleResponse.json()) as BundlesPageJson;
          const bundles = bundlePage.items.filter(({ id }) =>
            bundleIdsInOrder.includes(id)
          );
          const { ordersFeature, bundleFeature, multiMarkersOrderFeature } =
            createFeatureStructure(orders, bundles);
          setState({
            loading: false,
            refetching: false,
            data: {
              fetchedBundles: bundles,
              fetchedOrders: orders,
              orders: ordersFeature,
              bundles: bundleFeature,
              multiMarkers: multiMarkersOrderFeature,
              windowGroups: createGroupColors(uniqueWindowGroups(orders)),
            },
            error: '',
          });
        }
      } catch (error) {
        setState({ loading: false, refetching: false, error: error.message });
      }
    },
    [date, region]
  );

  // useEffect(() => {
  //   fetchData();
  // }, [fetchData]);

  const reFetch = useCallback(async () => {
    fetchData(true);
  }, [fetchData]);

  return {
    state,
    reFetch,
    setState,
  };
};

export function uniqueWindowGroups(orders: PlannerOrder[]): string[] {
  return orders.reduce((acc, cur) => {
    const { window } = cur;

    if (!acc.includes(window)) {
      acc.push(window);
    }
    return acc;
  }, []);
}
